<template>
  <div>
    <div class="w-full xl:w-1/3">
      <div>
        <fwb-input
          v-model="email"
          placeholder="j.doe@acme.com"
          label="Email"
          :disabled="!isNewUser()"
          :validation-status="hasError('email')">
          <template v-if="hasError('email')" #validationMessage>
            {{ errorText('email') }}
          </template>
        </fwb-input>
      </div>

      <div class="mt-4">
        <fwb-input
          v-model="firstName"
          placeholder="John"
          label="First Name"
          :validation-status="hasError('firstName')">
          <template v-if="hasError('firstName')" #validationMessage>
            {{ errorText('firstName') }}
          </template>
        </fwb-input>
      </div>

      <div class="mt-4">
        <fwb-input
          v-model="lastName"
          placeholder="Doe"
          label="Last Name"
          :validation-status="hasError('lastName')">
          <template v-if="hasError('lastName')" #validationMessage>
            {{ errorText('lastName') }}
          </template>
        </fwb-input>
      </div>

      <div v-if="isNewUser()"
        class="mt-4">
        <fwb-toggle
          v-model="sendInvitation"
          label="Send Invitation Email" />
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          You can send an invitation email to the team member or set a password for them without invitation.
        </p>
      </div>

      <div v-if="showPasswordFields()">
        <div class="mt-4">
          <fwb-input
            v-model="password"
            placeholder="••••••••"
            label="Password"
            :validation-status="hasError('password')"
            type="password">
            <template v-if="hasError('password')" #validationMessage>
              {{ errorText('password') }}
            </template>
          </fwb-input>
        </div>

        <div class="mt-4">
          <fwb-input
            v-model="passwordConfirmation"
            placeholder="••••••••"
            label="Password Confirmation"
            :validation-status="hasError('passwordConfirmation')"
            type="password">
            <template v-if="hasError('passwordConfirmation')" #validationMessage>
              {{ errorText('passwordConfirmation') }}
            </template>
          </fwb-input>
        </div>
      </div>
    </div>

    <div class="actions mt-10 border-t border-gray-200 pt-5">
      <div v-if="isNewUser()">
        <fwb-button
          class="px-5 py-2.5 me-2 mb-2"
          color="default"
          @click="inviteUser">

          {{ sendInvitation ? 'Invite Team Member' : 'Add Team Member' }}
        </fwb-button>

        <a
          href="/users"
          class="inline-block w-full sm:w-auto text-center text-gray-900 bg-white border border-gray-300 focus:outline-none hover:bg-gray-100 focus:ring-4 focus:ring-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-gray-800 dark:text-white dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600 dark:focus:ring-gray-700">
          Cancel
        </a>
      </div>

      <div v-else>
        <fwb-button
          class="px-5 py-2.5 me-2 mb-2"
          color="default"
          @click="updateUser">

          Update Profile Settings
        </fwb-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { FwbButton, FwbInput, FwbToggle } from 'flowbite-vue'
import securedAxios from '~/javascript/utils/axios'
import { toastError, toastSuccess } from '~/javascript/utils/toast'
import useValidation from '~/javascript/components/composables/validation'

const props = defineProps({
  user: {
    type: Object,
    required: true
  }
})

const axios = securedAxios()

const userId = ref(props.user.id)
const email = ref(props.user.email || '')
const firstName = ref(props.user.first_name || '')
const lastName = ref(props.user.last_name || '')
const password = ref('')
const passwordConfirmation = ref('')
const sendInvitation = ref(true)

const { updateErrors, hasError, errorText } = useValidation()

function isNewUser() {
  return !userId.value
}

function showPasswordFields() {
  return !sendInvitation.value || !isNewUser()
}

function collectUpdateParams() {
  const params = {
    first_name: firstName.value,
    last_name: lastName.value
  }

  if (password.value !== '' || passwordConfirmation.value !== '') {
    params.password = password.value
    params.password_confirmation = passwordConfirmation.value
  }

  return { user: params }
}

async function updateUser() {
  try {
    const params = collectUpdateParams()

    await axios.patch(`/users/${userId.value}`, params)

    toastSuccess('Your profile settings updated successfully', { duration: 1000 })

    setTimeout(() => {
      window.Turbo.visit('/')
    }, 1500)
  } catch (error) {
    updateErrors(error.response.data)
    toastError(error)
  }
}

function collectNewUserParams() {
  return {
    email: email.value,
    first_name: firstName.value,
    last_name: lastName.value,
    send_invitation: sendInvitation.value,
    password: password.value,
    password_confirmation: passwordConfirmation.value
  }
}

async function inviteUser() {
  try {
    const params = collectNewUserParams()

    await axios.post('/users', params)

    if (sendInvitation.value) {
      toastSuccess('Invitation email successfully sent')
    } else {
      toastSuccess('Team member successfully added')
    }

    setTimeout(() => {
      window.Turbo.visit('/users')
    }, 1500)
  } catch (error) {
    updateErrors(error.response.data)
    toastError(error)
  }
}
</script>

<style scoped>

</style>
